import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import { UNKNOWN_SUBSCRIPTION } from "@app/common/constants/billing";
import Tracker from "@app/utils/tracking/Tracker";

/**
 * Use this hook to perform various CRUD operations on a user's billing subscription
 */
const useBillingSubscription = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const billingAccountId = user?.billingAccount?.id;

  const invalidateBillingAccount = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["workspace"] }),
      queryClient.invalidateQueries({ queryKey: ["billing"] }),
    ]);
  };

  const getSubscriptionQuery = useQuery({
    queryKey: ["billing", "subscription", billingAccountId],
    queryFn: async () => {
      if (!billingAccountId) throw new Error("No billing account");

      const subscription =
        await apiClient.billing.getBillingSubscription(billingAccountId);

      Tracker.setPeopleProperties({
        plan: subscription.inTrialPeriod ? "trial" : subscription.plan,
        plan_cadence: subscription.billingCadence ?? "none",
      });

      return subscription;
    },
    enabled:
      Boolean(billingAccountId) &&
      Boolean(user?.billingAccount?.stripeCustomerId),
  });

  const updateSubscriptionQuantityMutation = useMutation({
    mutationFn: async (quantityDelta: number) => {
      if (!billingAccountId) throw new Error("No billing account");
      return apiClient.billing.updateSubscription(billingAccountId, {
        quantityDelta,
      });
    },
    onSuccess: invalidateBillingAccount,
    onError: (error) => {
      Sentry.captureException(error);
    },
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: async () => {
      if (!billingAccountId) throw new Error("No billing account");
      return apiClient.billing.cancelSubscription(billingAccountId);
    },
    onSettled: invalidateBillingAccount,
    onError: (error) => {
      Sentry.captureException(error);
    },
  });

  const reactivateSubscriptionMutation = useMutation({
    mutationFn: async () => {
      if (!billingAccountId) throw new Error("No billing account");
      return apiClient.billing.updateSubscription(billingAccountId, {
        reactivate: true,
      });
    },
    onSettled: invalidateBillingAccount,
    onError: (error) => Sentry.captureException(error),
  });

  return {
    isLoading: getSubscriptionQuery.isLoading,
    isError: getSubscriptionQuery.isError,
    subscription: getSubscriptionQuery.data ?? UNKNOWN_SUBSCRIPTION,
    updateSubscriptionQuantity: updateSubscriptionQuantityMutation,
    cancelSubscription: cancelSubscriptionMutation,
    reactivateSubscription: reactivateSubscriptionMutation,
  };
};

export default useBillingSubscription;
